<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  ArrowPathIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  CpuChipIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/24/outline'
import Pts2DeviceIcon from '../components/pts2/Pts2DeviceIcon.vue'
import Pts2DiagnosticsStrip from '../components/pts2/Pts2DiagnosticsStrip.vue'
import Pts2ControllerNav from '../components/pts2/Pts2ControllerNav.vue'
import { PTS2_CATEGORIES, controllerCount, deviceBindingLabel, deviceStateView, deviceFilterStates } from '../pts2/controllers.js'
import {
  configurationCaption,
  controllerConnection,
  controllerTime,
  useControllerPages,
} from '../pts2/useControllerPages.js'
import '../pts2/controllers.css'
import { useI18n } from '../i18n.js'
const { locale, t } = useI18n()
const categories = computed(() => PTS2_CATEGORIES.map((item) => ({ ...item,
  label: t(`pts2UiCategory_${item.key}`), tabLabel: t(`pts2UiTab_${item.key}`), singular: t(`pts2UiSingular_${item.key}`),
})))
const controllerId = Number(useRoute().params.controllerId)
const tabList = ref(null)
const {
  stationName,
  filters,
  controller,
  page,
  diagnostics,
  loading,
  refreshing,
  error,
  refresh,
  moreDevices,
} = useControllerPages(controllerId)
const category = computed(() => categories.value.find((item) => item.key === filters.category))
const stateView = (row) => deviceStateView(row, { ...page.value, failed: !!error.value }, t)
const problemCount = computed(
  () =>
    page.value?.items.filter((row) => !stateView(row).historical && ['ERROR', 'OFFLINE'].includes(row.state)).length ??
    0,
)
const states = computed(() => deviceFilterStates(filters.category)
  .map((state) => [state, t(`pts2UiDeviceFilter_${state}`)]))
function selectCategory(key) {
  if (key !== filters.category) Object.assign(filters, { search: '', state: '', category: key })
}
async function revealSelectedTab() {
  await nextTick()
  const list = tabList.value
  const selected = list?.querySelector('[aria-selected="true"]')
  if (!selected) return
  const bounds = list.getBoundingClientRect()
  const target = selected.getBoundingClientRect()
  if (target.left < bounds.left) list.scrollLeft -= bounds.left - target.left
  else if (target.right > bounds.right) list.scrollLeft += target.right - bounds.right
}
watch([() => filters.category, tabList], revealSelectedTab, { flush: 'post' })
onMounted(() => window.addEventListener('resize', revealSelectedTab))
onBeforeUnmount(() => window.removeEventListener('resize', revealSelectedTab))
function sort(field) {
  if (filters.sort === field) filters.direction = filters.direction === 'asc' ? 'desc' : 'asc'
  else {
    filters.sort = field
    filters.direction = 'asc'
  }
}
function ariaSort(field) {
  return filters.sort === field ? (filters.direction === 'asc' ? 'ascending' : 'descending') : 'none'
}
async function tabKey(event, index) {
  const target =
    event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? 4
        : event.key === 'ArrowRight'
          ? (index + 1) % 5
          : event.key === 'ArrowLeft'
            ? (index + 4) % 5
            : null
  if (target == null) return
  event.preventDefault()
  selectCategory(PTS2_CATEGORIES[target].key)
  await nextTick()
  document.getElementById(`pc-tab-${filters.category}`)?.focus()
}
</script>

<template>
  <section class="pc-page pc-detail" aria-labelledby="controller-title">
    <nav class="pc-breadcrumb" :aria-label="t('pts2UiBreadcrumb')">
      <RouterLink :to="{ name: 'pts2-controllers' }">{{ t('pts2UiControllers') }}</RouterLink
      ><ChevronRightIcon aria-hidden="true" /><span>{{ controller?.displayName ?? t('pts2UiDetails') }}</span>
    </nav>
    <header class="pc-heading pc-detail-heading">
      <div>
        <h1 id="controller-title">{{ controller?.displayName ?? t('pts2UiController') }}</h1>
        <p>
          {{ controller?.stableCode ?? '—' }} <span class="pc-station-caption">· {{ stationName }}</span>
        </p>
      </div>
      <div v-if="controller" class="pc-detail-connection">
        <span class="pc-state" :class="error ? 'unknown' : controllerConnection(controller.connectionFreshness, t).tone"
          ><i></i
          >{{ error ? t('pts2UiFreshnessUnconfirmed') : controllerConnection(controller.connectionFreshness, t).label }}</span
        ><small>{{ t('pts2UiLastData') }}: {{ controllerTime(controller.statusReceivedAt, locale) }}</small>
      </div>
      <div class="pc-heading-actions">
        <button class="pc-refresh" :disabled="refreshing" @click="refresh()">
          <ArrowPathIcon :class="{ spinning: refreshing }" aria-hidden="true" />{{ t('pts2UiRefresh') }}
        </button>
      </div>
    </header>
    <p v-if="error" class="pc-notice" role="alert">{{ error }} <button @click="refresh()">{{ t('pts2UiRetry') }}</button></p>
    <template v-if="controller">
      <Pts2DiagnosticsStrip :snapshot="diagnostics[controllerId]" :summary="controller" :failed="!!error" />
      <p class="pc-detail-config">
        {{ configurationCaption(controller, t) }} · {{ controllerTime(controller.configurationObservedAt, locale) }}
      </p>
      <Pts2ControllerNav :controller-id="controllerId" active="devices" />
      <div ref="tabList" class="pc-tabs" role="tablist" :aria-label="t('pts2UiDeviceCategories')">
        <button
          v-for="(item, index) in categories"
          :id="`pc-tab-${item.key}`"
          :key="item.key"
          role="tab"
          :aria-selected="filters.category === item.key"
          :tabindex="filters.category === item.key ? 0 : -1"
          aria-controls="pc-device-panel"
          @click="selectCategory(item.key)"
          @keydown="tabKey($event, index)"
        >
          <span
            ><Pts2DeviceIcon :category="item.key" /><strong>{{ item.tabLabel }}</strong
            ><b>{{ controllerCount(controller, item.key) ?? '—' }}</b></span
          ><small v-if="filters.category === item.key && problemCount" class="pc-problem-count"
            >{{ t('pts2UiProblemCount', { count: problemCount }) }}</small
          >
        </button>
      </div>
      <section
        id="pc-device-panel"
        class="pc-device-panel"
        role="tabpanel"
        :aria-labelledby="`pc-tab-${filters.category}`"
        :aria-busy="loading || refreshing"
        tabindex="0"
      >
        <div class="pc-table-toolbar">
          <h2>
            {{ category.label }} <span>· {{ controllerCount(controller, category.key) ?? '—' }}</span>
          </h2>
          <label class="pc-search"
            ><MagnifyingGlassIcon aria-hidden="true" /><input
              v-model="filters.search"
              type="search"
              maxlength="120"
              :aria-label="t('pts2UiDeviceSearch')"
              :placeholder="t('pts2UiDeviceSearchPlaceholder')" /></label
          ><select v-model="filters.state" :aria-label="t('pts2UiDeviceStatus')">
            <option value="">{{ t('pts2UiAllStates') }}</option>
            <option v-for="[value, label] in states" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <p
          v-if="page && (page.statusFreshness !== 'FRESH' || page.configurationState !== 'CONFIRMED')"
          class="pc-context-note"
        >
          {{ t('pts2UiHistoricalDeviceHelp') }}
        </p>
        <div v-if="loading || (refreshing && !page?.items.length)" class="pc-table-loading" role="status">
          {{ t('pts2UiLoadingDevices') }}
        </div>
        <div v-else-if="!page?.items.length" class="pc-empty">
          <Pts2DeviceIcon :category="category.key" />
          <h3>
            {{
              filters.search || filters.state
                ? t('pts2UiNoMatchingDevices')
                : controllerCount(controller, category.key) === 0
                  ? t('pts2UiNoCategoryDevices')
                  : t('pts2UiDeviceInventoryUnconfirmed')
            }}
          </h3>
          <p>
            {{
              filters.search || filters.state
                ? t('pts2UiDeviceFilterHelp')
                : controllerCount(controller, category.key) === 0
                  ? t('pts2UiNoCategoryDevicesHelp')
                  : t('pts2UiDeviceInventoryHelp')
            }}
          </p>
          <button
            v-if="filters.search || filters.state"
            @click="Object.assign(filters, { search: '', state: '' })"
          >
            {{ t('pts2UiResetFilters') }}
          </button>
        </div>
        <div v-else class="pc-table-scroll">
          <table class="pc-device-table">
            <thead>
              <tr>
                <th :aria-sort="ariaSort('deviceId')">
                  <button @click="sort('deviceId')">{{ t('pts2UiDevice') }} <ChevronUpDownIcon aria-hidden="true" /></button>
                </th>
                <th>{{ t(filters.category === 'ATG' ? 'pts2UiTank' : 'pts2UiBinding') }}</th>
                <th :aria-sort="ariaSort('protocol')">
                  <button @click="sort('protocol')">{{ t('pts2UiProtocol') }} <ChevronUpDownIcon aria-hidden="true" /></button>
                </th>
                <th :aria-sort="ariaSort('state')">
                  <button @click="sort('state')">{{ t('pts2UiState') }} <ChevronUpDownIcon aria-hidden="true" /></button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in page.items" :key="`${row.category}-${row.deviceId}`">
                <td>
                  <span class="pc-device-name"
                    ><Pts2DeviceIcon :category="row.category" /><strong
                      >{{ category.singular }} №{{ row.deviceId }}</strong
                    ></span
                  ><small v-if="row.portId != null" class="pc-port"
                    >{{ t('pts2UiPort') }} {{ row.portId
                    }}<template v-if="row.address != null"> · {{ t('pts2UiAddress') }} {{ row.address }}</template></small
                  >
                </td>
                <td>{{ deviceBindingLabel(row, t) }}</td>
                <td class="pc-protocol">
                  {{ row.protocolName || '—' }}<small v-if="row.protocolCode != null">{{ t('pts2UiCode') }} {{ row.protocolCode }}</small>
                </td>
                <td>
                  <span class="pc-state" :class="stateView(row).tone"><i></i>{{ stateView(row).label }}</span
                  ><small class="pc-state-detail">{{ stateView(row).detail }}</small>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <footer class="pc-table-footer">
          <span role="status"
            >{{ t('pts2UiDeviceCount', { count: page?.items.length ?? 0 }) }}<template v-if="page?.hasMore"> · {{ t('pts2UiMoreDevicesAvailable') }}</template></span
          ><button v-if="page?.nextCursor" :disabled="refreshing" @click="moreDevices">{{ t('pts2UiLoadMore') }}</button>
          <p>{{ t('pts2UiPerDeviceHelp') }}</p>
        </footer>
      </section>
    </template>
    <div v-else-if="loading" class="pc-table-loading" role="status">{{ t('pts2UiLoadingController') }}</div>
    <div v-else class="pc-empty">
      <CpuChipIcon aria-hidden="true" />
      <h2>{{ t('pts2UiControllerUnavailable') }}</h2>
      <RouterLink :to="{ name: 'pts2-controllers' }">{{ t('pts2UiBackToList') }}</RouterLink>
    </div>
    <p v-if="filters.category === 'READER'" class="pc-footnote">
      {{ t('pts2UiReaderHelp') }}
    </p>
  </section>
</template>
